/**
 * AdminReminderProfiles - Wave-style reminder profile management
 * 
 * Features:
 * - List all reminder profiles with their events
 * - Create/Edit/Delete profiles (admin only)
 * - Wave-style checkboxes for timing events
 * - Visual timeline preview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { reminderProfilesAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Clock,
  CalendarDays,
  AlertTriangle,
  Loader2,
  Lock,
  Check,
} from 'lucide-react';

// Preset reminder day options (Wave-style)
const BEFORE_DUE_OPTIONS = [14, 7, 3, 1];
const AFTER_DUE_OPTIONS = [1, 3, 7, 14, 21, 30];

// Form component for Wave-style checkboxes (extracted to avoid nested component)
const ReminderForm = ({ formData, setFormData }) => {
  const toggleBeforeDue = (days) => {
    setFormData(prev => ({
      ...prev,
      beforeDue: prev.beforeDue.includes(days)
        ? prev.beforeDue.filter(d => d !== days)
        : [...prev.beforeDue, days].sort((a, b) => b - a)
    }));
  };

  const toggleAfterDue = (days) => {
    setFormData(prev => ({
      ...prev,
      afterDue: prev.afterDue.includes(days)
        ? prev.afterDue.filter(d => d !== days)
        : [...prev.afterDue, days].sort((a, b) => a - b)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Profile Name */}
      <div className="space-y-2">
        <Label htmlFor="profile-name">Profile Name *</Label>
        <Input
          id="profile-name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Custom Net 15"
          data-testid="profile-name-input"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="profile-description">Description</Label>
        <Textarea
          id="profile-description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Optional description..."
          rows={2}
        />
      </div>

      {/* Before Due Date Reminders */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-500" />
          <Label className="text-sm font-medium">Before Due Date</Label>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {BEFORE_DUE_OPTIONS.map(days => (
            <button
              key={`before-${days}`}
              type="button"
              onClick={() => toggleBeforeDue(days)}
              className={`relative flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                formData.beforeDue.includes(days)
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
              data-testid={`before-due-${days}`}
            >
              <span className="font-medium">{days}d</span>
              {formData.beforeDue.includes(days) && (
                <Check className="absolute top-1 right-1 w-3 h-3 text-amber-500" />
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Selected: {formData.beforeDue.length > 0 ? formData.beforeDue.sort((a,b) => b-a).join(', ') + ' days before' : 'None'}
        </p>
      </div>

      {/* After Due Date Reminders */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <Label className="text-sm font-medium">After Due Date (Overdue)</Label>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {AFTER_DUE_OPTIONS.map(days => (
            <button
              key={`after-${days}`}
              type="button"
              onClick={() => toggleAfterDue(days)}
              className={`relative flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                formData.afterDue.includes(days)
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
              data-testid={`after-due-${days}`}
            >
              <span className="font-medium">{days}d</span>
              {formData.afterDue.includes(days) && (
                <Check className="absolute top-1 right-1 w-3 h-3 text-red-500" />
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Selected: {formData.afterDue.length > 0 ? formData.afterDue.sort((a,b) => a-b).join(', ') + ' days after' : 'None'}
        </p>
      </div>

      {/* Timeline Preview */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Reminder Timeline Preview</h4>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-200" />
          
          {/* Due date marker */}
          <div className="flex justify-center relative z-10">
            <div className="flex flex-col items-center">
              <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow" />
              <span className="text-xs font-medium text-blue-600 mt-1">Due</span>
            </div>
          </div>
          
          {/* Before markers */}
          <div className="flex justify-start gap-2 mt-4">
            {formData.beforeDue.sort((a,b) => b-a).map(days => (
              <div key={days} className="flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-xs text-amber-600">-{days}d</span>
              </div>
            ))}
          </div>
          
          {/* After markers */}
          <div className="flex justify-end gap-2 mt-2">
            {formData.afterDue.sort((a,b) => a-b).map(days => (
              <div key={days} className="flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-xs text-red-600">+{days}d</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminReminderProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    beforeDue: [],
    afterDue: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reminderProfilesAPI.getProfiles();
      setProfiles(response.items || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load reminder profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Convert events array to separate before/after arrays
  const eventsToForm = (events = []) => {
    const beforeDue = events.filter(e => e.timing === 'before' && e.enabled).map(e => e.days);
    const afterDue = events.filter(e => e.timing === 'after' && e.enabled).map(e => e.days);
    return { beforeDue, afterDue };
  };

  // Convert form arrays back to events array
  const formToEvents = (beforeDue, afterDue) => {
    const events = [];
    beforeDue.forEach(days => events.push({ timing: 'before', days, enabled: true }));
    afterDue.forEach(days => events.push({ timing: 'after', days, enabled: true }));
    return events;
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      beforeDue: [7, 1],  // Default: 7 days and 1 day before
      afterDue: [1, 7],   // Default: 1 day and 7 days after
    });
    setShowCreateModal(true);
  };

  const handleEdit = (profile) => {
    const { beforeDue, afterDue } = eventsToForm(profile.events);
    setFormData({
      name: profile.name,
      description: profile.description || '',
      beforeDue,
      afterDue,
    });
    setSelectedProfile(profile);
    setShowEditModal(true);
  };

  const handleDelete = (profile) => {
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    setSaving(true);
    try {
      const events = formToEvents(formData.beforeDue, formData.afterDue);
      await reminderProfilesAPI.createProfile({
        name: formData.name,
        description: formData.description || null,
        events,
      });
      toast.success('Reminder profile created');
      setShowCreateModal(false);
      fetchProfiles();
    } catch (err) {
      toast.error(err.message || 'Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!formData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    setSaving(true);
    try {
      const events = formToEvents(formData.beforeDue, formData.afterDue);
      await reminderProfilesAPI.updateProfile(selectedProfile.id, {
        name: formData.name,
        description: formData.description || null,
        events,
      });
      toast.success('Reminder profile updated');
      setShowEditModal(false);
      fetchProfiles();
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSaving(true);
    try {
      await reminderProfilesAPI.deleteProfile(selectedProfile.id);
      toast.success('Reminder profile deleted');
      setShowDeleteModal(false);
      fetchProfiles();
    } catch (err) {
      toast.error(err.message || 'Failed to delete profile');
    } finally {
      setSaving(false);
    }
  };

  const isDefaultProfile = (profile) => {
    return profile.is_default || ['reminder-prepay', 'reminder-net7', 'reminder-net30', 'reminder-net45'].includes(profile.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reminder-profiles">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Reminder Profiles</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure payment reminder schedules. Profiles are auto-assigned based on customer payment terms.
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="create-profile-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Profile
        </Button>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-blue-500 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800">How Reminder Profiles Work</p>
            <p className="text-blue-600 mt-1">
              When an invoice is issued, the system automatically assigns a reminder profile based on the customer&apos;s payment terms. 
              Staff can manually override the schedule on individual invoices.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Profiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles.map(profile => (
          <div
            key={profile.id}
            className="p-5 bg-white border rounded-lg hover:shadow-sm transition-shadow"
            data-testid={`profile-card-${profile.id}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-400" />
                <h3 className="font-semibold text-gray-900">{profile.name}</h3>
                {isDefaultProfile(profile) && (
                  <Badge variant="outline" className="text-xs bg-gray-50">
                    <Lock className="w-3 h-3 mr-1" />
                    Default
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(profile)}
                  data-testid={`edit-profile-${profile.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                {!isDefaultProfile(profile) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(profile)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-profile-${profile.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Description */}
            {profile.description && (
              <p className="text-sm text-gray-500 mb-3">{profile.description}</p>
            )}

            {/* Events */}
            <div className="space-y-2">
              {/* Before due */}
              <div className="flex flex-wrap gap-1">
                {profile.events
                  ?.filter(e => e.timing === 'before' && e.enabled)
                  .sort((a, b) => b.days - a.days)
                  .map(e => (
                    <Badge key={`before-${e.days}`} className="bg-amber-100 text-amber-700 border-amber-200">
                      <Clock className="w-3 h-3 mr-1" />
                      {e.days}d before
                    </Badge>
                  ))}
              </div>

              {/* After due */}
              <div className="flex flex-wrap gap-1">
                {profile.events
                  ?.filter(e => e.timing === 'after' && e.enabled)
                  .sort((a, b) => a.days - b.days)
                  .map(e => (
                    <Badge key={`after-${e.days}`} className="bg-red-100 text-red-700 border-red-200">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {e.days}d after
                    </Badge>
                  ))}
              </div>

              {(!profile.events || profile.events.length === 0) && (
                <p className="text-sm text-gray-400 italic">No reminder events configured</p>
              )}
            </div>

            {/* Stats */}
            <div className="mt-3 pt-3 border-t flex items-center text-xs text-gray-400">
              <CalendarDays className="w-3 h-3 mr-1" />
              {profile.events?.filter(e => e.enabled).length || 0} reminder event(s)
            </div>
          </div>
        ))}

        {profiles.length === 0 && (
          <div className="col-span-2 p-8 text-center bg-gray-50 rounded-lg border border-dashed">
            <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No reminder profiles found</p>
            <p className="text-sm text-gray-400">Click &quot;New Profile&quot; to create one</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              New Reminder Profile
            </DialogTitle>
          </DialogHeader>
          
          <ReminderForm formData={formData} setFormData={setFormData} />
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCreate} disabled={saving} data-testid="save-create-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Edit Reminder Profile
            </DialogTitle>
          </DialogHeader>
          
          <ReminderForm formData={formData} setFormData={setFormData} />
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent data-testid="delete-profile-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Reminder Profile?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedProfile?.name}&quot;? 
              This action cannot be undone. Existing invoices using this profile will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Profile
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminReminderProfiles;
